import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  ReactFlow,
  Background,
  useReactFlow,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import { setDashboardFilter, setPrevTabId } from "@/redux/slices/dashboardV2.slice";
import { cleanObject } from "@/utils/globalUtils";
import TileNode from "./TileNode";
import CanvasZoomControls from "@/components/Common/CanvasZoomControls";
import "@/styles/liveDashboard.css";

const nodeTypes = {
  tileItem: TileNode,
};

// Grid layout constants matching original react-grid-layout
const GRID_COLS = 16;
const MARGIN_X = 10;
const MARGIN_Y = 10;
const ROW_HEIGHT = 80;
// Virtual grid width used on screens narrower than 1200px (see invariant #4 in context doc)
const MIN_VIRTUAL_WIDTH = 1280;
// Overlap smaller than this (px) is treated as rounding noise, not a collision
const OVERLAP_TOLERANCE = 2;

const getAvailableWidth = (containerWidth) =>
  Math.max(containerWidth >= 1200 ? containerWidth : MIN_VIRTUAL_WIDTH, 1200);

const rectsOverlap = (a, b) =>
  Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > OVERLAP_TOLERANCE &&
  Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) > OVERLAP_TOLERANCE;

// Push tiles down (top-to-bottom, left-to-right) until no tile overlaps another.
// Returns a Map of id -> resolved rect.
const resolveOverlaps = (rects) => {
  const sorted = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed = [];
  sorted.forEach((rect) => {
    const r = { ...rect };
    let colliders = placed.filter((p) => rectsOverlap(r, p));
    while (colliders.length) {
      r.y = Math.max(...colliders.map((p) => p.y + p.h)) + MARGIN_Y;
      colliders = placed.filter((p) => rectsOverlap(r, p));
    }
    placed.push(r);
  });
  return new Map(placed.map((r) => [r.id, r]));
};

export default function TileDashboardViewport({
  gridData = [],
  setGridData,
  layouts = {},
  setLayouts,
  mode = "view",
  setEditItem = () => {},
  setComponentModal = () => {},
  setData = () => {},
  dashboardName = "",
  savedViewport,
  onViewportChange,
  onZoomChange,
}) {
  const dispatch = useDispatch();
  const { isDark } = useSelector((state) => state.theme);
  const { groupData = [], dashboardFilter = {}, dateRange = {}, refetch } = useSelector(
    (state) => state.dashboardV2
  );

  const containerRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1400
  );

  const [dialChangeKey, setDialChangeKey] = useState(0);
  const { setViewport, getViewport, fitView, zoomIn, zoomOut } = useReactFlow();
  const initializedRef = useRef(false);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 350 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 350 });
  }, [zoomOut]);

  // Measure container width so grid tiles fill the screen or stack responsively
  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      const w = containerRef.current?.clientWidth || (typeof window !== "undefined" ? window.innerWidth : 0);
      if (w > 0) {
        setContainerWidth(w);
      }
    };
    updateWidth();
    const ro = new ResizeObserver(() => updateWidth());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const isMobile = containerWidth < 768;
  // Width the canvas lays tiles out against. Pixel layouts store the width they were
  // saved at (baseWidth) so they can be rescaled to fit any screen.
  const availableWidth = getAvailableWidth(containerWidth);
  const nodesRef = useRef([]);
  // Uniform fit-to-width factor applied to all tiles on render. Saved layouts are
  // stored unscaled (rendered / fitScale) so they don't compound on the next render.
  const fitScaleRef = useRef(1);
  const toLayoutRect = useCallback(
    ({ x, y, width, height }) => {
      const s = fitScaleRef.current || 1;
      return {
        x: Math.round(x / s),
        y: Math.round(y / s),
        w: Math.round(width / s),
        h: Math.round(height / s),
        isPixel: true,
        baseWidth: availableWidth,
      };
    },
    [availableWidth]
  );

  // ── Tile action handlers ──────────────────────────────────────
  const handleCopy = useCallback(
    (item) => {
      Swal.fire({
        title: "Copy Tile?",
        text: "This will create a duplicate of this component.",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          const _id = uuid();
          // Use the rendered (already scaled) rect so grid-unit and pixel layouts copy alike
          const source = nodesRef.current.find((n) => n.id === String(item._id));
          const rect = toLayoutRect({
            x: (source?.position?.x ?? 50) + 40,
            y: (source?.position?.y ?? 50) + 40,
            width: source?.width ?? 360,
            height: source?.height ?? 280,
          });

          setGridData((prev) => [...prev, { ...item, _id, chartTitle: `${item.chartTitle} (copy)` }]);
          setLayouts((prev) => ({
            ...prev,
            lg: [...(prev?.lg || []), { i: _id, ...rect }],
          }));
        }
      });
    },
    [toLayoutRect, setGridData, setLayouts]
  );

  const handleRemove = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This tile will be permanently removed.",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          setGridData((prev) => prev.filter((d) => d._id !== id));
          setLayouts((prev) => ({
            ...prev,
            lg: (prev?.lg || []).filter((l) => String(l.i) !== String(id)),
          }));
        }
      });
    },
    [setGridData, setLayouts]
  );

  const handleEdit = useCallback(
    (tile) => {
      setEditItem(tile);
      setComponentModal(true);
    },
    [setEditItem, setComponentModal]
  );

  const selectHandler = useCallback(
    (selectId) => {
      const foundId = dashboardFilter?.selectedTiles?.find((id) => id === selectId);
      const selectedTiles = foundId
        ? dashboardFilter.selectedTiles.filter((id) => id !== selectId)
        : [...(dashboardFilter?.selectedTiles || []), selectId];
      dispatch(setDashboardFilter({ ...(dashboardFilter || {}), selectedTiles }));
    },
    [dashboardFilter, dispatch]
  );

  const handleLinkedTab = useCallback(
    (tabId) => {
      if (tabId) {
        setData((pre) => {
          const current = pre?.tabs?.find((item) => item.isSelected);
          dispatch(setPrevTabId(current?.tabId || null));
          const nextTabs = pre?.tabs?.map((item) => ({ ...item, isSelected: item.tabId === tabId }));
          return { ...pre, tabs: nextTabs };
        });
      }
    },
    [setData, dispatch]
  );

  // ── Drag & Resize synchronization with layouts ────────────────
  const handleResizeEnd = useCallback(
    (nodeId, { x, y, width, height }) => {
      if (isMobile) return;
      setDialChangeKey((prev) => prev + 1);
      setLayouts((prev) => {
        const prevLg = prev?.lg || [];
        const filtered = prevLg.filter((l) => String(l.i) !== String(nodeId));
        return {
          ...prev,
          lg: [...filtered, { i: nodeId, ...toLayoutRect({ x, y, width, height }) }],
        };
      });
    },
    [setLayouts, isMobile, toLayoutRect]
  );

  const handleNodeDragStop = useCallback(
    (_event, node) => {
      if (isMobile) return;
      setDialChangeKey((prev) => prev + 1);
      setLayouts((prev) => {
        const prevLg = prev?.lg || [];
        const width = node.measured?.width || node.style?.width || node.width || 340;
        const height = node.measured?.height || node.style?.height || node.height || 260;
        const filtered = prevLg.filter((l) => String(l.i) !== String(node.id));
        return {
          ...prev,
          lg: [
            ...filtered,
            { i: node.id, ...toLayoutRect({ x: node.position.x, y: node.position.y, width, height }) },
          ],
        };
      });
    },
    [setLayouts, isMobile, toLayoutRect]
  );

  // ── Filter & Display Data calculation ─────────────────────────
  const computeDisplayData = useCallback(
    (tile) => {
      const { selectedTiles = [], comparisonPeriods = [], window: filterWindow, ...rest } = dashboardFilter || {};
      const isSelected = selectedTiles?.includes(tile?._id);
      const cleanedRest = cleanObject(rest);

      let displayData = { ...tile, locations: { ...tile?.locations }, refetch };

      const currentItemFromGroup = groupData?.find((group) => (group.ids || []).includes(String(displayData?._id)));
      if (currentItemFromGroup?.parameters?.length) displayData.locations.yAxis = currentItemFromGroup.parameters;

      if (dateRange?.startDate && dateRange?.endDate) {
        displayData.timeRange = [dateRange.startDate, dateRange.endDate];
        displayData.xAxis = "custom";
      }

      if (isSelected) {
        const locations = { ...displayData.locations, ...cleanedRest.locations };
        displayData = { ...displayData, ...cleanedRest, locations };
        if (comparisonPeriods?.length) {
          displayData.comparisonPeriods = comparisonPeriods;
          displayData.window = filterWindow;
        }
      }

      return { displayData, isSelected };
    },
    [dashboardFilter, groupData, dateRange, refetch]
  );

  // ── React Flow Nodes builder (persists desktop layout on all devices) ────
  const buildNodes = useCallback(
    (currentNodes = []) => {
      const nodeMap = new Map(currentNodes.map((n) => [n.id, n]));
      const layoutList = layouts?.lg || (Array.isArray(layouts) ? layouts : layouts?.[Object.keys(layouts || {})[0]]) || [];

      // Base grid width: on screens < 1200 (mobile/tablet), use 1280 base width
      // so layout and spatial arrangements persist identically on mobile and desktop
      const colWidth = (availableWidth - MARGIN_X * (GRID_COLS + 1)) / GRID_COLS;

      // Pixel layouts saved before baseWidth existed: infer the width they were built on
      // from their right-most edge (the creator filled their screen), floored at the virtual width.
      const legacyPixelRight = layoutList.reduce(
        (max, l) => (l?.isPixel && !l.baseWidth ? Math.max(max, (l.x || 0) + (l.w || 0)) : max),
        0
      );
      const legacyBaseWidth = Math.max(MIN_VIRTUAL_WIDTH, legacyPixelRight + MARGIN_X);

      // Pass 1: compute every tile's rect in the current screen's coordinate space
      const rects = (gridData || []).map((tile, index) => {
        const id = String(tile?._id);
        const existing = nodeMap.get(id);
        const itemLayout = layoutList.find((l) => String(l.i) === id);
        const isCustom = Boolean(itemLayout?.isPixel || (itemLayout && itemLayout.w > 16));

        let x, y, w, h;

        if (isCustom) {
          // User has manually positioned or resized this tile. Scale horizontally from the
          // width it was saved at so it stays in proportion with grid tiles on any screen.
          const rawX = itemLayout.x ?? existing?.position?.x ?? 0;
          const rawW = itemLayout.w ?? existing?.style?.width ?? 340;
          const scaleX = availableWidth / (itemLayout.baseWidth || legacyBaseWidth);
          x = Math.round(rawX * scaleX);
          w = Math.max(140, Math.round((rawX + rawW) * scaleX) - x);
          y = itemLayout.y ?? existing?.position?.y ?? 0;
          h = itemLayout.h ?? existing?.style?.height ?? 260;
        } else if (itemLayout) {
          // Grid layout item from original react-grid-layout:
          x = Math.round(itemLayout.x * (colWidth + MARGIN_X) + MARGIN_X);
          y = Math.round(itemLayout.y * (ROW_HEIGHT + MARGIN_Y) + MARGIN_Y);
          w = Math.round(itemLayout.w * colWidth + Math.max(0, itemLayout.w - 1) * MARGIN_X);
          h = Math.round(itemLayout.h * ROW_HEIGHT + Math.max(0, itemLayout.h - 1) * MARGIN_Y);
        } else {
          // Default position in 4-column flow
          const col = index % 4;
          const row = Math.floor(index / 4);
          x = Math.round(col * 4 * (colWidth + MARGIN_X) + MARGIN_X);
          y = Math.round(row * 4 * (ROW_HEIGHT + MARGIN_Y) + MARGIN_Y);
          w = Math.round(4 * colWidth + 3 * MARGIN_X);
          h = Math.round(3 * ROW_HEIGHT + 2 * MARGIN_Y);
        }

        return { id, x, y, w, h };
      });

      // Pass 2: scale the whole layout uniformly so it fills the canvas width.
      // Every tile grows (or shrinks) by the same factor, keeping the arrangement intact.
      const contentRight = rects.reduce((max, r) => Math.max(max, r.x + r.w), 0);
      const rawFit = contentRight > 0 ? (availableWidth - MARGIN_X) / contentRight : 1;
      const fitScale = Math.abs(rawFit - 1) > 0.01 ? rawFit : 1;
      fitScaleRef.current = fitScale;
      const fitted = rects.map((r) => {
        const x = Math.round(r.x * fitScale);
        const y = Math.round(r.y * fitScale);
        return {
          id: r.id,
          x,
          y,
          w: Math.round((r.x + r.w) * fitScale) - x,
          h: Math.round((r.y + r.h) * fitScale) - y,
        };
      });

      // Pass 3: tiles must never overlap — push colliding tiles down
      const resolved = resolveOverlaps(fitted);

      return (gridData || []).map((tile) => {
        const id = String(tile?._id);
        const { x, y, w, h } = resolved.get(id);
        const { displayData, isSelected } = computeDisplayData(tile);

        const tileData = {
          tile,
          mode,
          isDark,
          isSelected,
          isMobile,
          displayData,
          dialChangeKey,
          dashboardName,
          onCopy: handleCopy,
          onEdit: handleEdit,
          onRemove: handleRemove,
          onSelect: selectHandler,
          onLinkedTab: handleLinkedTab,
          onResizeEnd: handleResizeEnd,
        };

        return {
          id,
          type: "tileItem",
          position: { x, y },
          width: w,
          height: h,
          initialWidth: w,
          initialHeight: h,
          measured: { width: w, height: h },
          style: { width: w, height: h, pointerEvents: "all" },
          draggable: mode !== "view" && !isMobile,
          selectable: true,
          data: tileData,
        };
      });
    },
    [
      gridData,
      layouts,
      availableWidth,
      isMobile,
      mode,
      isDark,
      computeDisplayData,
      dialChangeKey,
      dashboardName,
      handleCopy,
      handleEdit,
      handleRemove,
      selectHandler,
      handleLinkedTab,
      handleResizeEnd,
    ]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(() => buildNodes([]));

  useEffect(() => {
    setNodes((prevNodes) => buildNodes(prevNodes));
  }, [buildNodes, setNodes]);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // Micro-task to trigger layout calculation immediately upon mount/navigation
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  const defaultViewportRef = useRef(null);

  // Desktop tiles are already scaled to fill the canvas width, so a viewport saved on a
  // different screen (e.g. zoomed out on a small laptop) would leave empty space.
  // Desktop always opens at the natural fit; mobile keeps the saved viewport.
  const initialViewport = useMemo(() => {
    const hasSaved =
      savedViewport &&
      (savedViewport.x !== 0 || savedViewport.y !== 0 || savedViewport.zoom !== 1);
    return hasSaved && isMobile
      ? { x: savedViewport.x, y: savedViewport.y, zoom: savedViewport.zoom }
      : { x: 0, y: 0, zoom: 1 };
  }, [savedViewport, isMobile]);

  // ── Restore initial viewport on load ───────────────────────────
  useEffect(() => {
    if (initializedRef.current) return;

    defaultViewportRef.current = initialViewport;
    initializedRef.current = true;
    setViewport(initialViewport);
  }, [initialViewport, setViewport]);

  const handleResetToDefault = useCallback(() => {
    if (isMobile) {
      // On mobile: fit the complete dashboard within the viewable area of the user's screen
      let minX = Infinity;
      let minY = Infinity;
      let maxRight = 0;
      let maxBottom = 0;
      nodes.forEach((n) => {
        const x = n.position?.x ?? 0;
        const y = n.position?.y ?? 0;
        const r = x + (n.measured?.width || n.width || 340);
        const b = y + (n.measured?.height || n.height || 260);
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (r > maxRight) maxRight = r;
        if (b > maxBottom) maxBottom = b;
      });
      if (minX === Infinity) minX = 0;
      if (minY === Infinity) minY = 0;

      const totalW = Math.max(maxRight - minX, 300);

      const targetZoom = Math.min((containerWidth - 24) / totalW, 1);
      const finalX = ((containerWidth - totalW * targetZoom) / 2) - (minX * targetZoom);
      const finalY = 15 - (minY * targetZoom);

      setViewport(
        {
          x: Math.round(finalX),
          y: Math.round(finalY),
          zoom: Math.round(targetZoom * 100) / 100,
        },
        { duration: 400 }
      );
    } else {
      // On desktop: reset to the natural fit (tiles already fill the canvas width)
      setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 400 });
    }
  }, [isMobile, nodes, containerWidth, setViewport]);

  const lastReportedZoomRef = useRef(initialViewport.zoom);
  const viewportTimerRef = useRef(null);

  // Do not trigger parent re-renders during active zoom/pan animation frames
  const handleMove = useCallback(() => {}, []);

  const handleMoveEnd = useCallback(
    (_event, viewport) => {
      if (onZoomChange && Math.abs(viewport.zoom - lastReportedZoomRef.current) > 0.001) {
        lastReportedZoomRef.current = viewport.zoom;
        onZoomChange(viewport.zoom);
      }
      if (onViewportChange) {
        if (viewportTimerRef.current) {
          clearTimeout(viewportTimerRef.current);
        }
        viewportTimerRef.current = setTimeout(() => {
          onViewportChange(viewport);
        }, 600);
      }
    },
    [onZoomChange, onViewportChange]
  );

  useEffect(() => {
    return () => {
      if (viewportTimerRef.current) {
        clearTimeout(viewportTimerRef.current);
      }
    };
  }, []);

  // Smooth custom double-click / double-tap zoom that prevents browser page-level zoom
  const performSmoothZoom = useCallback(
    (clientX, clientY) => {
      const currentVp = getViewport();
      // Smooth incremental zoom (1.45x) or cycle back to 1.0x if already zoomed in (>= 2.2x)
      const nextZoom = currentVp.zoom >= 2.2 ? 1 : Math.min(2.5, Math.round(currentVp.zoom * 1.45 * 100) / 100);

      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds) return;

      const clickX = clientX - bounds.left;
      const clickY = clientY - bounds.top;

      if (nextZoom === 1) {
        handleResetToDefault();
      } else {
        const pointX = (clickX - currentVp.x) / currentVp.zoom;
        const pointY = (clickY - currentVp.y) / currentVp.zoom;

        const newX = clickX - pointX * nextZoom;
        const newY = clickY - pointY * nextZoom;

        setViewport({ x: newX, y: newY, zoom: nextZoom }, { duration: 450 });
      }
    },
    [getViewport, setViewport, fitView]
  );

  const handleCanvasDoubleClick = useCallback(
    (event) => {
      // Don't intercept double clicks on controls or interactive elements
      if (
        event.target.closest(".react-flow__controls") ||
        event.target.closest("button") ||
        event.target.closest("input") ||
        event.target.closest("textarea") ||
        event.target.closest(".nodrag")
      ) {
        return;
      }
      // Prevent browser default actions (such as text selection or whole page zoom)
      event.preventDefault();
      event.stopPropagation();
      performSmoothZoom(event.clientX, event.clientY);
    },
    [performSmoothZoom]
  );

  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const handleTouchEnd = useCallback(
    (event) => {
      if (event.touches?.length > 0) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const now = Date.now();
      const timeDiff = now - lastTapRef.current.time;
      const distDiff = Math.hypot(
        touch.clientX - lastTapRef.current.x,
        touch.clientY - lastTapRef.current.y
      );

      if (timeDiff > 40 && timeDiff < 320 && distDiff < 35) {
        if (
          event.target.closest(".react-flow__controls") ||
          event.target.closest("button") ||
          event.target.closest("input")
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        performSmoothZoom(touch.clientX, touch.clientY);
        lastTapRef.current = { time: 0, x: 0, y: 0 };
      } else {
        lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };
      }
    },
    [performSmoothZoom]
  );

  const memoizedNodeTypes = useMemo(() => nodeTypes, []);

  // Calculate content bounds and translate extent so zooming out stays centered
  // and prevents canvas from drifting into the corner or infinite void
  const contentBounds = useMemo(() => {
    let maxRight = availableWidth;
    let maxBottom = 800;
    nodes.forEach((n) => {
      const r = (n.position?.x || 0) + (n.measured?.width || n.width || 340);
      const b = (n.position?.y || 0) + (n.measured?.height || n.height || 260);
      if (r > maxRight) maxRight = r;
      if (b > maxBottom) maxBottom = b;
    });
    return {
      width: Math.max(maxRight, availableWidth),
      height: Math.max(maxBottom, 800),
    };
  }, [nodes, containerWidth]);

  const dynamicCanvasHeight = useMemo(() => {
    // Dynamic height based on content bounds so all dashboard items fit without getting cut off
    const minH = typeof window !== "undefined" ? (isMobile ? window.innerHeight * 0.75 : window.innerHeight * 0.78) : 700;
    const contentH = (contentBounds.height || 0) + 70;
    return Math.round(Math.max(contentH, minH));
  }, [contentBounds.height, isMobile]);

  const translateExtent = useMemo(() => {
    // Generous padding so fitView and zooming out on mobile/desktop never hit clamping conflicts
    // that would cut off the dashboard or shift it to a corner
    const padX = Math.max(contentBounds.width * 2, 2500);
    const padY = Math.max(contentBounds.height * 2, 2500);
    return [
      [-padX, -padY],
      [contentBounds.width + padX, contentBounds.height + padY],
    ];
  }, [contentBounds]);

  return (
    <div
      ref={containerRef}
      className="live-dashboard-canvas-container"
      style={{
        position: "relative",
        width: "100%",
        minHeight: dynamicCanvasHeight,
        touchAction: "none",
      }}
      onDoubleClick={handleCanvasDoubleClick}
      onTouchEnd={handleTouchEnd}
    >
      <div
        ref={canvasWrapperRef}
        className="live-dashboard-canvas"
        style={{
          width: "100%",
          height: dynamicCanvasHeight,
          border: `1px solid ${isDark ? "#2d2d2d" : "#dee2e6"}`,
          borderRadius: 8,
          overflow: "hidden",
          background: isDark ? "#1a1a1a" : "#fafbfc",
          position: "relative",
          touchAction: "none",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={[]}
          nodeTypes={memoizedNodeTypes}
          onNodesChange={onNodesChange}
          onNodeDragStop={handleNodeDragStop}
          onMove={handleMove}
          onMoveEnd={handleMoveEnd}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={mode !== "view" && !isMobile}
          nodesConnectable={false}
          elementsSelectable={true}
          onNodeClick={() => {}}
          panOnDrag={true}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          minZoom={0.1}
          maxZoom={2.5}
          fitViewOptions={{ padding: 0.04, maxZoom: 1 }}
          translateExtent={translateExtent}
          defaultViewport={initialViewport}
          snapToGrid={true}
          snapGrid={[10, 10]}
          deleteKeyCode={null}
          selectionKeyCode={null}
          multiSelectionKeyCode={null}
        >
          <Background
            variant="dots"
            color={isDark ? "#3b3b3b" : "#cbd5e1"}
            gap={20}
            size={1.2}
          />
        </ReactFlow>
        <CanvasZoomControls
          canvasRef={canvasWrapperRef}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleResetToDefault}
        />
      </div>
    </div>
  );
}

TileDashboardViewport.propTypes = {
  gridData: PropTypes.array,
  setGridData: PropTypes.func.isRequired,
  layouts: PropTypes.object,
  setLayouts: PropTypes.func.isRequired,
  mode: PropTypes.string,
  setEditItem: PropTypes.func,
  setComponentModal: PropTypes.func,
  setData: PropTypes.func,
  dashboardName: PropTypes.string,
  savedViewport: PropTypes.object,
  onViewportChange: PropTypes.func,
  onZoomChange: PropTypes.func,
};
